import React from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '../../../../components';
import type { AutomatedStat } from '../../../../mock-data/types';
import { StatIcon } from './StatIcon';
import { StatsPanel, StatsGrid, Stat, StatBody, StatValue, StatLabel } from './AutomatedStats.styles';

export type AutomatedStatsProps = {
  stats: AutomatedStat[];
  /** While true, the figures show placeholders (the endpoint is loading). */
  loading?: boolean;
};

/**
 * "Automated Operations" — the row of operational totals (campaigns run,
 * players targeted / activated, notifications sent, support cases) that Lumio
 * has handled automatically. The section title is owned by the page. Each stat
 * pairs a grey Figma glyph (StatIcon) with an orange figure + muted label. The
 * glyph + label are static chrome; only the figure loads in.
 */
export const AutomatedStats = ({ stats, loading = false }: AutomatedStatsProps): React.ReactElement => {
  const { t } = useTranslation();
  // The stat figures are sample metrics (left raw); the labels are a fixed
  // closed chrome vocabulary, so localise them here at the render site (the
  // values are sourced from mock-data and reach us as plain strings).
  const STAT_LABELS: Record<string, string> = {
    Campaigns: t('Campaigns'),
    'Targeted Players': t('Targeted Players'),
    'Sent Notifications': t('Sent Notifications'),
    'Activated Players': t('Activated Players'),
    'Support Cases': t('Support Cases'),
  };

  return (
    <StatsPanel>
      <StatsGrid>
        {stats.map((stat) => (
          <Stat key={stat.id}>
            <StatIcon name={stat.icon} size={30} />
            <StatBody>
              {loading ? (
                <Skeleton width={Math.max(40, stat.value.length * 13)} height={20} radius={6} />
              ) : (
                <StatValue text={stat.value} tag="span" />
              )}
              <StatLabel text={STAT_LABELS[stat.label] ?? stat.label} tag="span" />
            </StatBody>
          </Stat>
        ))}
      </StatsGrid>
    </StatsPanel>
  );
};

export default AutomatedStats;
